import type {
  TransformationSceneFactory,
  TransformationSceneHandle,
} from "./three/scene-factory";
import {
  cloneCameraSnapshots,
  createDefaultCameraSnapshots,
} from "./camera-snapshots";
import type { CameraSnapshots } from "./camera-snapshots";
import type { Dimension } from "@src/math/types";
import type { RenderState } from "./render-state";

export interface VisualizationSource {
  getRenderState(now: number): RenderState;
  isAnimating(): boolean;
  subscribe(listener: () => void): () => void;
  completePlayback(): boolean;
  pausePlayback(): void;
  resetPlayback(): void;
}

interface RunningViewport {
  readonly scene: TransformationSceneHandle;
  readonly unsubscribe: () => void;
}

export type ViewportPhase =
  | { readonly kind: "preparing" }
  | { readonly kind: "active" }
  | { readonly kind: "rendered" }
  | {
      readonly kind: "stopped";
      readonly reason: "failed" | "disposed";
    };

type RunningPhase = Extract<ViewportPhase, { kind: "active" | "rendered" }> &
  RunningViewport;
type RuntimePhase =
  | Extract<ViewportPhase, { kind: "preparing" | "stopped" }>
  | RunningPhase;

function isRunningPhase(phase: RuntimePhase): phase is RunningPhase {
  return phase.kind === "active" || phase.kind === "rendered";
}

export class TransformationViewportRuntime {
  private phase: RuntimePhase = { kind: "preparing" };
  private animationFrame: number | null = null;
  private removeEventListeners: (() => void) | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly source: VisualizationSource,
    private readonly cameraSnapshots: { current: CameraSnapshots },
    private readonly onPhaseChange: (phase: ViewportPhase) => void,
    private readonly onFailure: (error: unknown) => void,
    private readonly createScene: TransformationSceneFactory,
  ) {}

  start(): void {
    if (this.phase.kind !== "preparing") return;
    this.onPhaseChange({ kind: "preparing" });
    this.removeEventListeners = bindViewportEvents(this.canvas, {
      activate: this.activate,
      contextLost: this.contextLost,
      visibility: this.visibility,
    });
    let scene: TransformationSceneHandle | null = null;
    try {
      const loadedScene = this.createScene(this.canvas, this.schedule);
      scene = loadedScene;
      loadedScene.restoreCameraSnapshots(this.cameraSnapshots.current);
      const unsubscribe = this.source.subscribe(this.schedule);
      this.phase = {
        kind: "active",
        scene: loadedScene,
        unsubscribe,
      };
      this.onPhaseChange({ kind: "active" });
      this.schedule();
    } catch (error) {
      try {
        scene?.dispose();
      } catch {
        // The original initialization failure is the actionable error.
      }
      this.fail(error);
    }
  }

  dispose(): void {
    this.stop("disposed");
  }

  getCameraSnapshots(): CameraSnapshots {
    if (!isRunningPhase(this.phase))
      return cloneCameraSnapshots(this.cameraSnapshots.current);
    try {
      const snapshots = cloneCameraSnapshots(
        this.phase.scene.getCameraSnapshots(),
      );
      this.cameraSnapshots.current = snapshots;
      return cloneCameraSnapshots(snapshots);
    } catch (error) {
      this.fail(error);
      return cloneCameraSnapshots(this.cameraSnapshots.current);
    }
  }

  restoreCameraSnapshots(snapshots: CameraSnapshots): void {
    const nextSnapshots = cloneCameraSnapshots(snapshots);
    this.cameraSnapshots.current = nextSnapshots;
    this.runIfRunning((scene) => {
      scene.restoreCameraSnapshots(nextSnapshots);
    });
  }

  resetView(dimension: Dimension): void {
    const defaults = createDefaultCameraSnapshots();
    this.cameraSnapshots.current =
      dimension === 2
        ? { ...this.cameraSnapshots.current, 2: defaults[2] }
        : { ...this.cameraSnapshots.current, 3: defaults[3] };
    this.runIfRunning((scene) => {
      scene.resetView(dimension);
    });
  }

  private runIfRunning(
    action: (scene: TransformationSceneHandle) => void,
  ): void {
    if (!isRunningPhase(this.phase)) return;
    try {
      action(this.phase.scene);
      this.schedule();
    } catch (error) {
      this.fail(error);
    }
  }

  private readonly schedule = (): void => {
    if (
      !isRunningPhase(this.phase) ||
      this.animationFrame !== null ||
      document.visibilityState === "hidden"
    )
      return;
    this.animationFrame = requestAnimationFrame(this.render);
  };

  private readonly render = (now: number): void => {
    this.animationFrame = null;
    if (!isRunningPhase(this.phase)) return;
    const running = this.phase;
    try {
      this.source.completePlayback();
      const needsAnotherFrame = running.scene.render(
        this.source.getRenderState(now),
      );
      if (running.kind === "active") {
        this.canvas.dataset.rendered = "true";
        this.phase = { ...running, kind: "rendered" };
        this.onPhaseChange({ kind: "rendered" });
      }
      if (this.source.isAnimating() || needsAnotherFrame) this.schedule();
    } catch (error) {
      this.fail(error);
    }
  };

  private readonly activate = (): void => {
    this.schedule();
  };

  private readonly visibility = (): void => {
    if (document.visibilityState === "hidden") {
      this.cancelAnimationFrame();
      this.source.pausePlayback();
    } else this.schedule();
  };

  private readonly contextLost = (): void => {
    if (!isRunningPhase(this.phase)) return;
    this.source.resetPlayback();
    this.fail(new Error("WebGL context was lost"));
  };

  private fail(error: unknown): void {
    if (this.phase.kind === "stopped") return;
    if (isRunningPhase(this.phase)) {
      try {
        this.cameraSnapshots.current = cloneCameraSnapshots(
          this.phase.scene.getCameraSnapshots(),
        );
      } catch {
        // Preserve the last known camera state if the scene is already broken.
      }
    }
    this.stop("failed");
    this.onFailure(error);
  }

  private stop(reason: "failed" | "disposed"): void {
    if (this.phase.kind === "stopped") return;
    this.cancelAnimationFrame();
    if (isRunningPhase(this.phase)) this.releaseRunning(this.phase);
    this.removeEventListeners?.();
    this.removeEventListeners = null;
    delete this.canvas.dataset.rendered;
    this.phase = { kind: "stopped", reason };
    this.onPhaseChange(this.phase);
  }

  private releaseRunning(running: RunningPhase): void {
    running.unsubscribe();
    try {
      running.scene.dispose();
    } catch (error) {
      console.error("Failed to dispose transformation viewport", error);
    }
  }

  private cancelAnimationFrame(): void {
    if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }
}

interface ViewportEventCallbacks {
  readonly activate: () => void;
  readonly contextLost: () => void;
  readonly visibility: () => void;
}

function bindViewportEvents(
  canvas: HTMLCanvasElement,
  callbacks: ViewportEventCallbacks,
): () => void {
  document.addEventListener("visibilitychange", callbacks.visibility);
  canvas.addEventListener("pointerdown", callbacks.activate);
  canvas.addEventListener("wheel", callbacks.activate, { passive: true });
  canvas.addEventListener("touchstart", callbacks.activate, {
    passive: true,
  });
  canvas.addEventListener("webglcontextlost", callbacks.contextLost);
  window.addEventListener("resize", callbacks.activate);
  window.addEventListener("pageshow", callbacks.activate);
  return () => {
    canvas.removeEventListener("pointerdown", callbacks.activate);
    canvas.removeEventListener("wheel", callbacks.activate);
    canvas.removeEventListener("touchstart", callbacks.activate);
    canvas.removeEventListener("webglcontextlost", callbacks.contextLost);
    window.removeEventListener("resize", callbacks.activate);
    window.removeEventListener("pageshow", callbacks.activate);
    document.removeEventListener("visibilitychange", callbacks.visibility);
  };
}
